import { ElementKey } from '@/types/component';
import { GenericFunctionComponent } from 'base/components/GenericComponent';
import { upperFirst } from 'lodash-es';

type NameGeneratorFunction<Args extends any[] | [] = []> = (...args: Args) => string;

const subPathToName = (inputString: string) => {
  const unwantedParts = ['.tsx', '.ts'];
  const unwantedPartsRegExp = new RegExp(`\\b(${unwantedParts.join('|')})\\b`, 'gi');

  const parts = inputString.split(/[/:]+/);
  const filteredParts = parts.filter((part) => part);
  const transformedParts = filteredParts.map((part) => upperFirst(part).replace(unwantedPartsRegExp, ''));

  return transformedParts.join('');
};

const defaultNameGenerator: NameGeneratorFunction = () => {
  const error = new Error();
  if (error.stack) {
    const stack = error.stack.split('\n');

    const callerPathRegexp = /\/(content|element)\//;
    const lastLineWithCallerPath = stack.reverse().find((line) => callerPathRegexp.test(line));

    if (lastLineWithCallerPath) {
      const matches = lastLineWithCallerPath.match(/\/(content|element|layout)\/(.*?)\.tsx/);
      if (matches && matches.length) {
        return subPathToName(matches[0]);
      }
    }
  }

  return '';
};

export const withComponentDisplayName = <P extends ElementKey, NameGeneratorArgs extends any[] | [] = []>(
  componentFunc: GenericFunctionComponent<P>,
  options: {
    fallbackDisplayName?: string;
    nameGenerator?: NameGeneratorFunction<NameGeneratorArgs>;
    nameGeneratorArgs?: NameGeneratorArgs;
  }
): void => {
  if (Object.hasOwn(componentFunc, 'displayName')) return;

  const { fallbackDisplayName, nameGenerator, nameGeneratorArgs } = options || {};
  const nameGeneratorFunc = nameGenerator || defaultNameGenerator;

  const displayName =
    nameGeneratorFunc(...((nameGeneratorArgs as NameGeneratorArgs) || [])) || fallbackDisplayName || 'GenericComponent';

  Object.defineProperty(componentFunc, 'displayName', { value: displayName, writable: true });
};
